import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EuroIcon from "@mui/icons-material/Euro";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WeekendIcon from "@mui/icons-material/Weekend";
import CelebrationIcon from "@mui/icons-material/Celebration";
import pricingImage from "../assets/images/tarieven-puppy.png";

const StyledPricingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
}));

const StyledPricingSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  maxWidth: 800,
  marginLeft: "auto",
  marginRight: "auto",
}));

const StyledPricingImage = styled("img")<{ $isMobile: boolean }>(
  ({ theme, $isMobile }) => ({
    width: "100%",
    height: $isMobile ? 300 : 430,
    objectFit: "cover",
    borderRadius: theme.spacing(1.5),
    boxShadow: theme.shadows[2],
    display: "block",
  })
);

const StyledPricingPaper = styled(Paper)<{ $isDarkMode: boolean }>(
  ({ theme, $isDarkMode }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1.5),
    backgroundColor: $isDarkMode
      ? "#3D3D3D"
      : theme.palette.background.paper,
    color: $isDarkMode ? "#F0F0F0" : theme.palette.text.primary,
  })
);

const StyledInfoPaper = styled(Paper)<{ $isDarkMode: boolean }>(
  ({ theme, $isDarkMode }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    backgroundColor: $isDarkMode
      ? "#3D3D3D"
      : theme.palette.secondary.light,
    boxShadow: "none",
  })
);

const PriceRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "36px minmax(0, 1fr) 145px",
  columnGap: theme.spacing(1.5),
  alignItems: "center",
  padding: theme.spacing(2.15, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": {
    borderBottom: "none",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "32px minmax(0, 1fr)",
    alignItems: "start",
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const PriceText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "1.05rem",
  lineHeight: 1.35,
  textAlign: "right",
  [theme.breakpoints.down("sm")]: {
    gridColumn: 2,
    textAlign: "left",
    marginTop: theme.spacing(0.5),
  },
}));

type PricingRowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  price: string;
};

const PricingRow: React.FC<PricingRowProps> = ({
  icon,
  title,
  description,
  price,
}) => (
  <PriceRow>
    <IconBox>{icon}</IconBox>

    <Box>
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, lineHeight: 1.35, mb: 0.25 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.45 }}
      >
        {description}
      </Typography>
    </Box>

    <PriceText>{price}</PriceText>
  </PriceRow>
);

export const PricingSection: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <StyledPricingSection
      id="pricing"
      component="section"
      aria-labelledby="pricing-heading"
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          id="pricing-heading"
          color="primary.main"
        >
          Onze tarieven
        </Typography>

        <StyledPricingSubtitle variant="body1" align="center">
          Duidelijke tarieven voor dagopvang, logeren en feestdagen. Zo weet je
          vooraf precies waar je aan toe bent.
        </StyledPricingSubtitle>

        <Grid container spacing={{ xs: 4, md: 4 }} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <StyledPricingImage
              src={pricingImage}
              alt="Honden ontspannen tijdens hun verblijf bij Pippaya"
              $isMobile={isMobile}
            />

            <StyledInfoPaper $isDarkMode={isDarkMode}>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 500, mb: 1.5 }}
              >
                Goed om te weten
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.65 }}
              >
                Voor logeren geldt dat een hond op de eerste gereserveerde dag
                na 14:00 uur kan worden gebracht en op de laatste gereserveerde
                dag vóór 10:00 uur wordt opgehaald.
                <br />
                <br />
                Afwijkende breng- of ophaaltijden zijn alleen mogelijk in overleg
                en wanneer dit vooraf bij de boeking is afgesproken. Hiervoor geldt
                €5,00 per extra uur. Bij onaangekondigd later ophalen kan de extra
                tijd eveneens in rekening worden gebracht.
                <br />
                <br />
                De eigenaar brengt voor een verblijf voldoende eigen voeding mee,
                zodat de hond het vertrouwde voer kan blijven eten. Normale
                medicatie die eenvoudig met de maaltijd of op een afgesproken tijd
                kan worden gegeven, is inbegrepen. Voor intensieve of aanvullende
                verzorging kunnen in overleg extra kosten gelden.
              </Typography>
            </StyledInfoPaper>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledPricingPaper elevation={2} $isDarkMode={isDarkMode}>
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  mb: 1.25,
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                Tarievenoverzicht
              </Typography>

              <Box aria-label="Prijslijst voor hondenopvang">
                <PricingRow
                  icon={<EuroIcon fontSize="small" />}
                  title="Dagopvang"
                  description="Brengen vanaf 08:00 uur en ophalen vóór 18:00 uur"
                  price="€37,50"
                />

                <PricingRow
                  icon={<WeekendIcon fontSize="small" />}
                  title="1 t/m 5 nachten"
                  description="Brengen na 14:00 uur en ophalen vóór 10:00 uur"
                  price="€40,00"
                />

                <PricingRow
                  icon={<CalendarMonthIcon fontSize="small" />}
                  title="6 nachten of meer"
                  description="Brengen na 14:00 uur en ophalen vóór 10:00 uur"
                  price="€35,00"
                />

                <PricingRow
                  icon={<CelebrationIcon fontSize="small" />}
                  title="Feestdagopvang"
                  description="Brengen vanaf 10:00 uur. Eventueel inclusief één overnachting; ophalen vóór 10:00 uur de volgende ochtend."
                  price="€50,00"
                />

                <PricingRow
                  icon={<EuroIcon fontSize="small" />}
                  title="Afwijkende breng- en ophaaltijden"
                  description="Alleen mogelijk in overleg en wanneer deze vooraf bij de boeking zijn afgesproken"
                  price="€5,00 per extra uur"
                />
              </Box>
            </StyledPricingPaper>
          </Grid>
        </Grid>
      </Container>
    </StyledPricingSection>
  );
};
